import React, { useState, useEffect } from 'react';
import { Home, User, Users } from 'lucide-react';
import { createInvitation, setVisitingContext, addPodToPlan } from '../../services/plannerApi';
import type { AddPodResult } from '../../services/plannerApi';
import { usePodsStore } from '../../stores/usePodsStore';
import { trackEvent } from '../../hooks/useAnalytics';
import type { PodListItem } from '../../services/podsApi';
import './WhoPicker.css';

const WHO_PREF_KEY = 'vizday_who_preference';

// Plan-type-specific labels (§1.2.1 architectural constraint).
// Travel is the default for Phase 1. Future plan types override these.
// Plan-type-specific labels (§1.2.1 architectural constraint).
// Travel is the default for Phase 1. Future plan types override these.
const PLAN_TYPE_CONFIG = {
  travel: {
    question: "Who's this trip for?",
    companions: { label: 'Add companions', desc: 'Plan together with the people coming with you', Icon: Users },
    pod: { label: 'Add a Pod', desc: 'Include everyone from one of your Pods', Icon: Users },
    visiting: { label: 'Add who you\'re visiting', desc: 'Family or friends at the destination', Icon: Home },
    solo: { label: 'Just me', desc: 'Solo trip', Icon: User },
    sharePrompt: 'Share this link with your group',
    visitingPrompt: 'Who are you visiting?',
    visitingPlaceholder: 'e.g., Mom and Dad, college friends',
    confirmedCompanions: 'Group trip',
    confirmedPod: 'Pod trip',
    confirmedVisiting: 'Visiting',
    confirmedSolo: 'Solo trip',
    prefCompanions: 'Group trip',
    prefPod: 'Pod trip',
    prefVisiting: 'Visiting someone',
    prefSolo: 'Solo',
  },
};

type Phase = 'choosing' | 'invite_link' | 'pod_picker' | 'pod_confirmed' | 'visiting_input' | 'answered';

interface WhoPickerProps {
  itineraryId: number | null;
  onAnswered: (answer: 'companions' | 'visiting' | 'solo') => void;
}

const WhoPicker: React.FC<WhoPickerProps> = ({ itineraryId, onAnswered }) => {
  const config = PLAN_TYPE_CONFIG.travel; // Phase 1: always travel
  const [phase, setPhase] = useState<Phase>('choosing');
  const [answer, setAnswer] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [visitingText, setVisitingText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [podResult, setPodResult] = useState<AddPodResult | null>(null);

  // Pod store — fetch up front so the Pod option only shows when it can
  // actually be used (new users have no Pods; a dead-end option is worse
  // than no option).
  const { pods, fetchPods } = usePodsStore();
  useEffect(() => {
    fetchPods().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const activePods = pods.filter(p => p.my_status === 'active');

  // Check for stored preference
  const storedPref = (() => {
    try { return localStorage.getItem(WHO_PREF_KEY); } catch { return null; }
  })();

  const savePref = (pref: string) => {
    try { localStorage.setItem(WHO_PREF_KEY, pref); } catch { /* */ }
  };

  const handleCompanions = async () => {
    savePref('companions');
    if (!itineraryId) {
      setAnswer('companions');
      setPhase('answered');
      onAnswered('companions');
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await createInvitation(itineraryId);
      setShareUrl(result.share_url);
      setPhase('invite_link');
    } catch {
      // Still advance even if invitation fails
      setAnswer('companions');
      setPhase('answered');
      onAnswered('companions');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePod = async () => {
    savePref('pod');
    // Fetch pods if not loaded
    await fetchPods();
    setPhase('pod_picker');
  };

  const handlePodSelect = async (pod: PodListItem) => {
    if (!itineraryId) {
      setAnswer(pod.name);
      setPhase('answered');
      onAnswered('companions');
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await addPodToPlan(itineraryId, pod.id);
      trackEvent('plan_shared', { method: 'pod', itinerary_id: itineraryId });
      setPodResult(result);
      setPhase('pod_confirmed');
    } catch {
      // Still advance if it fails
      setAnswer(pod.name);
      setPhase('answered');
      onAnswered('companions');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePodContinue = () => {
    setAnswer(podResult?.pod_name || 'Pod');
    setPhase('answered');
    onAnswered('companions');
  };

  const handleVisiting = () => {
    savePref('visiting');
    setPhase('visiting_input');
  };

  const handleVisitingSubmit = async () => {
    if (!visitingText.trim()) return;
    setIsSubmitting(true);
    if (itineraryId) {
      try {
        await setVisitingContext(itineraryId, visitingText.trim());
      } catch { /* still advance */ }
    }
    setAnswer(visitingText.trim());
    setPhase('answered');
    setIsSubmitting(false);
    onAnswered('visiting');
  };

  const handleSolo = () => {
    savePref('solo');
    setAnswer('solo');
    setPhase('answered');
    // Auto-advance after 500ms
    setTimeout(() => onAnswered('solo'), 500);
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      // Funnel step: plan → shared. Counted on the copy, not on link
      // creation — a link that was minted and never copied distributed
      // nothing, and counting it inflated the metric.
      trackEvent('plan_shared', { method: 'invite_link', itinerary_id: itineraryId });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard API not available */ }
  };

  const handleContinuePlanning = () => {
    setAnswer('companions');
    setPhase('answered');
    onAnswered('companions');
  };

  // Choosing phase
  if (phase === 'choosing') {
    return (
      <div className="chat-message assistant">
        <div className="message-avatar">V</div>
        <div className="who-picker">
          <div className="who-picker__title">{config.question}</div>
          {storedPref && (
            <div className="who-picker__pref-hint">
              Last time: {storedPref === 'companions' ? config.prefCompanions : storedPref === 'visiting' ? config.prefVisiting : config.prefSolo}
            </div>
          )}
          <button
            className="who-picker__option"
            onClick={handleCompanions}
            disabled={isSubmitting}
          >
            <span className="who-picker__emoji"><config.companions.Icon size={20} aria-hidden="true" /></span>
            <div className="who-picker__option-text">
              <span className="who-picker__option-label">{config.companions.label}</span>
              <span className="who-picker__option-desc">{config.companions.desc}</span>
            </div>
          </button>
          {activePods.length > 0 && (
            <button
              className="who-picker__option"
              onClick={handlePod}
              disabled={isSubmitting}
            >
              <span className="who-picker__emoji"><config.pod.Icon size={20} aria-hidden="true" /></span>
              <div className="who-picker__option-text">
                <span className="who-picker__option-label">{config.pod.label}</span>
                <span className="who-picker__option-desc">{config.pod.desc}</span>
              </div>
            </button>
          )}
          <button
            className="who-picker__option"
            onClick={handleVisiting}
            disabled={isSubmitting}
          >
            <span className="who-picker__emoji"><config.visiting.Icon size={20} aria-hidden="true" /></span>
            <div className="who-picker__option-text">
              <span className="who-picker__option-label">{config.visiting.label}</span>
              <span className="who-picker__option-desc">{config.visiting.desc}</span>
            </div>
          </button>
          <button
            className="who-picker__option"
            onClick={handleSolo}
            disabled={isSubmitting}
          >
            <span className="who-picker__emoji"><config.solo.Icon size={20} aria-hidden="true" /></span>
            <div className="who-picker__option-text">
              <span className="who-picker__option-label">{config.solo.label}</span>
              <span className="who-picker__option-desc">{config.solo.desc}</span>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Invite link phase
  if (phase === 'invite_link') {
    return (
      <div className="chat-message assistant">
        <div className="message-avatar">V</div>
        <div className="who-picker">
          <div className="who-picker__title">{config.sharePrompt}</div>
          {shareUrl && (
            <div className="who-picker__invite-link">
              <code className="who-picker__link-text">{shareUrl}</code>
              <button className="who-picker__copy-btn" onClick={handleCopyLink}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          )}
          <button className="who-picker__continue-btn" onClick={handleContinuePlanning}>
            Continue planning
          </button>
        </div>
      </div>
    );
  }

  // Pod picker phase
  if (phase === 'pod_picker') {
    return (
      <div className="chat-message assistant">
        <div className="message-avatar">V</div>
        <div className="who-picker">
          <div className="who-picker__title">Choose a Pod</div>
          {activePods.length === 0 ? (
            <div className="who-picker__empty-pods">
              <p className="who-picker__empty-text">You don't have any Pods yet.</p>
              <button className="who-picker__back-btn" onClick={() => setPhase('choosing')}>Back</button>
            </div>
          ) : (
            <div className="who-picker__pod-list">
              {activePods.map(pod => (
                <button
                  key={pod.id}
                  className="who-picker__pod-option"
                  onClick={() => handlePodSelect(pod)}
                  disabled={isSubmitting}
                >
                  <div className="who-picker__pod-avatars">
                    {pod.member_previews.slice(0, 3).map(m => (
                      <span key={m.id} className="who-picker__pod-avatar">{m.initials}</span>
                    ))}
                  </div>
                  <div className="who-picker__pod-info">
                    <span className="who-picker__pod-name">{pod.name}</span>
                    <span className="who-picker__pod-count">{pod.member_count} member{pod.member_count !== 1 ? 's' : ''}</span>
                  </div>
                </button>
              ))}
              <button className="who-picker__back-btn" onClick={() => setPhase('choosing')}>Back</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Pod confirmed phase
  if (phase === 'pod_confirmed' && podResult) {
    return (
      <div className="chat-message assistant">
        <div className="message-avatar">V</div>
        <div className="who-picker">
          <div className="who-picker__pod-confirmed">
            <span className="who-picker__pod-check">&#10003;</span>
            <span className="who-picker__pod-confirmed-name">{podResult.pod_name}</span>
            <span className="who-picker__pod-confirmed-count">
              {podResult.members_invited} member{podResult.members_invited !== 1 ? 's' : ''} invited
              {podResult.already_invited > 0 && ` · ${podResult.already_invited} already on this plan`}
            </span>
          </div>
          <button className="who-picker__continue-btn" onClick={handlePodContinue}>
            Continue planning
          </button>
        </div>
      </div>
    );
  }

  // Visiting input phase
  if (phase === 'visiting_input') {
    return (
      <div className="chat-message assistant">
        <div className="message-avatar">V</div>
        <div className="who-picker">
          <div className="who-picker__title">{config.visitingPrompt}</div>
          <form
            className="who-picker__visiting-form"
            onSubmit={(e) => { e.preventDefault(); handleVisitingSubmit(); }}
          >
            <input
              className="who-picker__visiting-input"
              type="text"
              value={visitingText}
              onChange={(e) => setVisitingText(e.target.value)}
              placeholder={config.visitingPlaceholder}
              autoFocus
              disabled={isSubmitting}
            />
            <button
              className="who-picker__visiting-submit"
              type="submit"
              disabled={!visitingText.trim() || isSubmitting}
            >
              Done
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Answered phase
  return (
    <div className="chat-message assistant">
      <div className="message-avatar">V</div>
      <div className="who-picker who-picker--answered">
        {answer === 'solo' && (
          <div className="who-picker__confirmed">&#10003; {config.confirmedSolo}</div>
        )}
        {answer === 'companions' && !podResult && (
          <div className="who-picker__confirmed">&#10003; {config.confirmedCompanions}</div>
        )}
        {podResult && (
          <div className="who-picker__confirmed">&#10003; {config.confirmedPod} · {podResult.pod_name}</div>
        )}
        {answer && answer !== 'solo' && answer !== 'companions' && (
          <div className="who-picker__confirmed">&#10003; {config.confirmedVisiting} {answer}</div>
        )}
      </div>
    </div>
  );
};

export default WhoPicker;
